import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Button as MuiButton } from '@mui/material';
import { toast } from '../../ui/toast';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { verifyMfa } from '../../utils/api_service/mfa_api';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { clearPending, takeToken, urlCarriesToken } from '../../utils/auth/mfaHandoff';

export const MfaChallenge: React.FC = () => {
  const history = useHistory();
  // Password login leaves the challenge in sessionStorage; an SSO login hands
  // it over in the URL (?token=&next=). Either way the deep link is kept so
  // the user lands where they were headed once the code is accepted.
  const [{ token: challengeToken, next }] = useState(() => takeToken('challenge'));
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    // No pending challenge: send the user back to sign-in (in an effect, not
    // during render). Otherwise drop a URL-carried token from the address bar.
    if (!challengeToken) history.push('/login');
    else if (urlCarriesToken()) history.replace('/auth/mfa');
  }, [challengeToken, history]);
  if (!challengeToken) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await verifyMfa(challengeToken, useRecovery ? { recoveryCode: code } : { code });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast.error('That code was not accepted. Please try again.', { position: 'bottom-center' });
      return;
    }
    clearPending('challenge');
    history.push(await finishSignIn(data, { deepLink: next }));
  };

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
          pt: 5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Two-factor verification
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {useRecovery ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </Typography>
        <TextInput
          type='text'
          name='code'
          label={useRecovery ? 'Recovery code' : 'Authentication code'}
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
        />
        <Button type='submit' label='Verify' />
        <MuiButton
          type='button'
          onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            textDecoration: 'underline',
            alignSelf: 'flex-start',
            p: 0,
            minWidth: 'auto',
            textTransform: 'none',
            '&:hover': {
              textDecoration: 'underline',
              bgcolor: 'transparent',
              color: 'text.primary',
            },
          }}
        >
          {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
        </MuiButton>
      </Box>
    </AuthenticationUiLayout>
  );
};
